import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Zap } from "lucide-react";
import { useLenis, scrollToSection } from "@/App";

const LINKS = [
    { id: "home", label: "Home", testId: "nav-link-home" },
    { id: "subscription", label: "Subscription Plan", testId: "nav-link-subscription-plan" },
    { id: "records", label: "Records", testId: "nav-link-records" },
    { id: "free-tip", label: "Free Daily Tip", testId: "nav-link-free-daily-tip" },
    { id: "contact", label: "Contact Us", testId: "nav-link-contact-us" },
];

export const Navbar = () => {
    const lenisRef = useLenis();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate(`/#${id}`);
        } else {
            scrollToSection(lenisRef.current, id);
            window.history.replaceState(null, "", `/#${id}`);
        }
    };

    return (
        <header
            data-testid="header-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled ? "bg-[#05070A]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-[72px] flex items-center justify-between">
                <button
                    data-testid="brand-logo-bettingnator"
                    onClick={() => go("home")}
                    className="flex items-center gap-2 group"
                >
                    <span className="w-8 h-8 rounded-lg bg-bn-blue/15 border border-bn-blue/40 flex items-center justify-center group-hover:bg-bn-blue/25 transition-colors">
                        <Zap className="w-4 h-4 text-bn-blue" />
                    </span>
                    <span className="font-display font-extrabold tracking-tight text-lg text-white">
                        BETTING<span className="text-bn-blue">NATOR</span>
                    </span>
                </button>

                <nav className="hidden lg:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={l.testId}
                            onClick={() => go(l.id)}
                            className="text-sm font-medium text-slate-400 hover:text-white transition-colors relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-bn-blue hover:after:w-full after:transition-all after:duration-300"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        data-testid="nav-cta-view-plans"
                        onClick={() => go("subscription")}
                        className="ml-2 px-5 py-2.5 rounded-full bg-bn-blue hover:bg-bn-bluehover text-white text-sm font-semibold tracking-wide transition-all duration-300 hover:shadow-[0_0_25px_rgba(0,102,255,0.5)] hover:-translate-y-0.5"
                    >
                        VIEW PLANS
                    </button>
                </nav>

                <button
                    data-testid="nav-mobile-menu-button"
                    onClick={() => setOpen(!open)}
                    className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-white/10 text-slate-300"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden overflow-hidden bg-[#05070A]/95 backdrop-blur-xl border-b border-white/10"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {LINKS.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`mobile-${l.testId}`}
                                    onClick={() => go(l.id)}
                                    className="text-left text-base font-medium text-slate-300 hover:text-white py-2"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                data-testid="mobile-nav-cta-view-plans"
                                onClick={() => go("subscription")}
                                className="mt-2 px-5 py-3 rounded-full bg-bn-blue text-white text-sm font-semibold"
                            >
                                VIEW PLANS
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
